import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Premium Stats (Animated Counters)
 * - Place this section after your Hero and before Services.
 * - Tailwind-based, responsive, accessible.
 *
 * Customize the items array below to change numbers / labels / icons.
 */

export default function Stats() {
  const items = useMemo(
    () => [
      {
        label: "Zufriedene Kund:innen",
        value: 100,
        suffix: "+",
        icon: UsersIcon,
      },
      {
        label: "Abgeschlossene Projekte",
        value: 250,
        suffix: "+",
        icon: CheckBadgeIcon,
      },
      {
        label: "Zufriedenheitsrate",
        value: 100,
        suffix: "%",
        icon: StarIcon,
      },
      {
        label: "Jahre Erfahrung",
        value: 3,
        suffix: "+",
        icon: ShieldIcon,
      },
    ],
    []
  );

  return (
    <section
      aria-labelledby="stats-title"
      className="relative isolate py-14 sm:py-18"
    >
      {/* subtle top border and background tint for separation */}
      <div className="absolute inset-0 -z-10 bg-white" aria-hidden />
      <div className="mx-auto max-w-6xl px-6 lg:px-12">
        {/* Section header (visually subtle for elegance) */}
        <header className="mb-8 text-center">
          <p className="text-[11px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-emerald-600">
            Unsere Kennzahlen
          </p>
          <h2
            id="stats-title"
            className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900"
          >
            Verlässliche Qualität, messbare Ergebnisse
          </h2>
        </header>

        {/* Grid */}
        <ul
          role="list"
          className="grid gap-5 sm:gap-6 grid-cols-2 md:grid-cols-4"
        >
          {items.map((it, i) => (
            <li key={i}>
              <StatCard {...it} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------------- Components ---------------- */

function StatCard({ label, value, suffix = "", icon: Icon }) {
  const { ref, inViewOnce } = useInViewOnce({ threshold: 0.4 });
  const prefersReduced = usePrefersReducedMotion();

  return (
    <article
      ref={ref}
      className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Top icon chip */}
      <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-600 shadow-sm">
        <Icon className="h-5 w-5" />
      </div>

      {/* Number */}
      <div className="flex items-baseline gap-1">
        {prefersReduced ? (
          <span className="text-3xl sm:text-4xl font-extrabold text-gray-900 tabular-nums">
            {value}
          </span>
        ) : (
          <CountUp
            className="text-3xl sm:text-4xl font-extrabold text-gray-900 tabular-nums"
            to={value}
            duration={1400}
            start={inViewOnce ? 0 : 0}
            play={inViewOnce}
          />
        )}
        <span className="text-emerald-600 text-2xl sm:text-3xl font-extrabold">
          {suffix}
        </span>
      </div>

      {/* Label */}
      <p className="mt-1 text-sm sm:text-[15px] text-gray-600">{label}</p>

      {/* Accent underline on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-0 h-[3px] w-0 bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-500 group-hover:w-full"
      />
    </article>
  );
}

/* ---------------- Hooks & Utils ---------------- */

function useInViewOnce({ threshold = 0.3 } = {}) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (!ref.current || seen) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen, threshold]);

  return { ref, inViewOnce: seen };
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(!!mq.matches);
    update();
    try {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    } catch {
      // Safari fallback
      mq.addListener?.(update);
      return () => mq.removeListener?.(update);
    }
  }, []);
  return reduced;
}

function CountUp({ to = 0, start = 0, duration = 1200, play = true, className }) {
  const spanRef = useRef(null);

  useEffect(() => {
    if (!play || !spanRef.current) return;

    const el = spanRef.current;
    const t0 = performance.now();
    const end = Number(to) || 0;
    const begin = Number(start) || 0;
    const d = Math.max(200, duration);

    let raf = 0;
    const step = (now) => {
      const p = Math.min(1, (now - t0) / d);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      const val = Math.round(begin + (end - begin) * eased);
      el.textContent = val.toString();
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, start, duration, play]);

  return <span ref={spanRef} className={className} aria-live="polite" />;
}

/* ---------------- Inline Icons (no libs) ---------------- */

function UsersIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function CheckBadgeIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 12l2 2 4-4" />
      <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
    </svg>
  );
}

function StarIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 17.27l6.18 3.73-1.64-7.03L21 9.24l-7.19-.62L12 2 10.19 8.62 3 9.24l4.46 4.73-1.64 7.03L12 17.27z" />
    </svg>
  );
}

function ShieldIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l8 3v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}
